import React, { useEffect, useRef, useState } from 'react';
import { useLogger } from '../hooks/useLogger';
import { useClerkToken } from 'src/hooks/useClerkToken';
import { WebSocketContext } from 'src/contexts/WebSocketContext';
import { parseControlMessage } from 'src/contracts';
import type { ControlClientMessage } from 'src/contracts';
import { addWsBreadcrumb } from 'src/lib/wsErrorBreadcrumbs';

export interface WebSocketProviderProps {
    children: React.ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
    const { logInfo, logError, traceId } = useLogger();
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const reconnectAttemptRef = useRef(0);
    const authFailedRef = useRef(false);
    const [connected, setConnected] = useState(false);
    const { getToken, isLoaded, isSignedIn } = useClerkToken('dashboard-jwt');
    const heartbeatIntervalMs = 25000;
    const reconnectBaseDelayMs = 500;
    const reconnectMaxDelayMs = 10000;

    useEffect(() => {
        let canceled = false;

        const clearReconnectTimeout = () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        const clearHeartbeatInterval = () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }
        };

        const sendHeartbeat = () => {
            const socket = socketRef.current;
            if (!socket) {
                return;
            }
            if (socket.readyState !== WebSocket.OPEN) {
                return;
            }
            const heartbeatMessage: ControlClientMessage = {
                type: 'ws_ping',
                trace_id: traceId,
            };
            socket.send(JSON.stringify(heartbeatMessage));
            addWsBreadcrumb({ event: 'message_out', direction: 'out', ws_type: heartbeatMessage.type });
        };

        const startHeartbeatInterval = () => {
            clearHeartbeatInterval();
            heartbeatIntervalRef.current = setInterval(() => {
                sendHeartbeat();
            }, heartbeatIntervalMs);
        };

        const scheduleReconnect = () => {
            if (canceled || reconnectTimeoutRef.current || authFailedRef.current) {
                return;
            }
            const delay = Math.min(
                reconnectBaseDelayMs * 2 ** reconnectAttemptRef.current,
                reconnectMaxDelayMs
            );
            reconnectAttemptRef.current += 1;
            reconnectTimeoutRef.current = setTimeout(() => {
                reconnectTimeoutRef.current = null;
                connect();
            }, delay);
            addWsBreadcrumb({ event: 'reconnect_scheduled' });
            logInfo(`WebSocket reconnect scheduled in ${delay}ms.`, {
                event: 'ws.reconnect.scheduled',
                fields: { duration_ms: delay },
            });
        };

        const connect = () => {
            if (canceled) {
                return;
            }
            if (!isLoaded || !isSignedIn) {
                return;
            }
            if (socketRef.current?.readyState === WebSocket.OPEN) {
                return;
            }
            if (socketRef.current?.readyState === WebSocket.CONNECTING) {
                return;
            }

            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const wsUrl = apiUrl.replace(/^http/, 'ws') + "/ws";
            const socket = new WebSocket(wsUrl);
            addWsBreadcrumb({ event: 'connect' });
            socketRef.current = socket;

            socket.addEventListener("open", async () => {
                addWsBreadcrumb({ event: 'open' });
                logInfo("WebSocket connected.", { event: 'ws.connected' });
                try {
                    const token = await getToken();
                    if (!token) {
                        logError("Missing auth token for WebSocket.", { event: 'ws.auth.missing' });
                        authFailedRef.current = true;
                        socket.close();
                        return;
                    }
                    socket.send(JSON.stringify({ type: "auth", token, trace_id: traceId }));
                    addWsBreadcrumb({ event: "auth_send" });
                } catch (error) {
                    addWsBreadcrumb({ event: "error" });
                    logError("Failed to authenticate WebSocket.", {
                        event: 'ws.auth.failed',
                        fields: { error: String(error) },
                    });
                    authFailedRef.current = true;
                    socket.close();
                }
            });

            socket.addEventListener("message", (event) => {
                addWsBreadcrumb({ event: "message_in", direction: "in" });
                let payload: unknown;
                try {
                    payload = JSON.parse(event.data);
                } catch (error) {
                    addWsBreadcrumb({ event: "parse_failed", direction: "in" });
                    logError('WebSocket message parse failed.', {
                        event: 'ws.message.invalid',
                        fields: { error: String(error) },
                    });
                    return;
                }
                const parsed = parseControlMessage(payload);
                if (!parsed.ok) {
                    logError('WebSocket message validation failed.', {
                        event: 'ws.message.invalid',
                        fields: {
                            contract: {
                                source: parsed.err.context?.source,
                                code: parsed.err.code,
                                handler: parsed.err.context?.handler,
                                type: parsed.err.context?.type,
                            },
                            issues: parsed.err.issues,
                        },
                    });
                    return;
                }
                if ('type' in parsed.value && parsed.value.type === "auth" && 'status' in parsed.value && parsed.value.status === "ok") {
                    setConnected(true);
                    addWsBreadcrumb({ event: "auth_ok" });
                    reconnectAttemptRef.current = 0;
                    clearReconnectTimeout();
                    authFailedRef.current = false;
                    startHeartbeatInterval();
                    sendHeartbeat();
                    return;
                }
                if ('type' in parsed.value && parsed.value.type === 'ws_pong') {
                    addWsBreadcrumb({ event: 'message_in', direction: 'in', ws_type: parsed.value.type });
                    return;
                }
                const customEvent = new CustomEvent('websocket-message', { detail: parsed.value });
                window.dispatchEvent(customEvent);
            });

            socket.addEventListener("close", (event) => {
                addWsBreadcrumb({ event: "close", close_code: event.code, close_reason: event.reason });
                setConnected(false);
                clearHeartbeatInterval();
                logInfo("WebSocket closed.", { event: 'ws.closed' });
                scheduleReconnect();
            });

            socket.addEventListener("error", (error) => {
                addWsBreadcrumb({ event: "error" });
                setConnected(false);
                clearHeartbeatInterval();
                logError(`WebSocket error: ${JSON.stringify(error)}`, { event: 'ws.error' });
                scheduleReconnect();
            });
        };

        const initialDelayMs = 600;
        reconnectAttemptRef.current = 0;
        authFailedRef.current = false;
        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connect();
        }, initialDelayMs);

        return () => {
            canceled = true;
            clearReconnectTimeout();
            clearHeartbeatInterval();
            if (socketRef.current) {
                logInfo("Closing WebSocket connection.", { event: 'ws.closing' });
                setConnected(false);
                socketRef.current.close();
                socketRef.current = null;
            }
        };
    }, [getToken, isLoaded, isSignedIn, logInfo, logError, traceId]);

    const sendMessage = (message: ControlClientMessage): boolean => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            addWsBreadcrumb({ event: "message_out", direction: "out", ws_type: message.type });
            return true;
        } else {
            logError('WebSocket is not connected.', { event: 'ws.send.failed' });
            return false;
        }
    };

    const socketValue = { sendMessage, connected };

    return (
        <WebSocketContext.Provider value={socketValue}>
            {children}
        </WebSocketContext.Provider>
    );
}

export default WebSocketProvider;
